import React from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Sparkles, Users, Target, Heart } from "lucide-react";
import { Card, CardContent } from "../components/ui/Card";

const AboutPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen py-20 px-6">
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <div className="text-center mb-16">
          <img
            src="https://customer-assets-m6fa6gv7.emergentagent.net/job_3563a477-4b96-448c-b228-bf27feeae015/artifacts/a3flx1m9_logo-mytharion%20no%20nome.png"
            alt="Mytharion"
            className="w-20 h-20 mx-auto mb-6 opacity-90"
          />
          <h1 className="font-display text-5xl md:text-6xl text-gold mb-6" data-testid="about-title">
            {t("about.title")}
          </h1>
          <p className="font-serif-fantasy italic text-lg text-parchment/90">
            {t("about.quote")}
          </p>
        </div>

        <Card className="mb-12">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <Target className="w-8 h-8 text-gold" />
              <h2 className="font-heading text-2xl text-gold">{t("about.missionTitle")}</h2>
            </div>

            <div className="space-y-5 text-muted-foreground leading-relaxed">
              <p>{t("about.mission1")}</p>
              <p>{t("about.mission2")}</p>
              <p>{t("about.mission3")}</p>

              <h3 className="font-heading text-xl text-gold pt-4">{t("about.indieTitle")}</h3>

              <p>{t("about.indie1")}</p>
              <p>{t("about.indie2")}</p>
              <p>{t("about.indie3")}</p>
              <p>
                <Link to="https://ko-fi.com/mytharion">
                  <button className="px-6 py-3 bg-gradient-to-b from-yellow-600 to-yellow-800 hover:from-yellow-500 hover:to-yellow-700 text-black font-heading rounded-lg gold-glow transition-all">
                    {t("about.donateCta")}
                  </button>
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Values */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-gold" />
            </div>
            <h3 className="font-heading text-lg text-foreground mb-2">
              {t("about.innovation")}
            </h3>
            <p className="text-sm text-muted-foreground">{t("about.innovationDesc")}</p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-purple-400" />
            </div>
            <h3 className="font-heading text-lg text-foreground mb-2">{t("about.community")}</h3>
            <p className="text-sm text-muted-foreground">{t("about.communityDesc")}</p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-ruby/20 flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-ruby" />
            </div>
            <h3 className="font-heading text-lg text-foreground mb-2">{t("about.passion")}</h3>
            <p className="text-sm text-muted-foreground">{t("about.passionDesc")}</p>
          </div>
        </div>

        {/* Story */}
        <Card className="mb-12">
          <CardContent className="p-8">
            <h2 className="font-heading text-2xl text-gold mb-6">{t("about.storyTitle")}</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>{t("about.story1")}</p>
              <p>{t("about.story2")}</p>
              <p>{t("about.story3")}</p>
              <p>
                {t("about.story4Prefix")}
                <Link to="/product/cartomancer_basic" className="text-gold hover:underline">
                  Cartomancer
                </Link>
                {t("about.story4Suffix")}
              </p>
              <p>{t("about.story5")}</p>
              <p>{t("about.story6")}</p>
              <p>{t("about.story7")}</p>
              <p>{t("about.story8")}</p>
            </div>
          </CardContent>
        </Card>

        {/* Roadmap */}
        <div className="rune-border bg-card/50 backdrop-blur rounded-xl p-8 mb-12">
          <h2 className="font-heading text-2xl text-gold mb-6">{t("about.roadmap")}</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-3 h-3 rounded-full bg-gold mt-1.5"></div>
              <div>
                <p className="font-heading text-foreground">{t("about.roadmapQ1")}</p>
                <p className="text-sm text-muted-foreground">{t("about.roadmapQ1Desc")}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-3 h-3 rounded-full bg-purple-500 mt-1.5"></div>
              <div>
                <p className="font-heading text-foreground">{t("about.roadmapQ2")}</p>
                <p className="text-sm text-muted-foreground">{t("about.roadmapQ2Desc")}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-3 h-3 rounded-full bg-muted mt-1.5"></div>
              <div>
                <p className="font-heading text-foreground">{t("about.roadmapQ3")}</p>
                <p className="text-sm text-muted-foreground">{t("about.roadmapQ3Desc")}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-3 h-3 rounded-full bg-muted mt-1.5"></div>
              <div>
                <p className="font-heading text-foreground">{t("about.roadmapQ4")}</p>
                <p className="text-sm text-muted-foreground">{t("about.roadmapQ4Desc")}</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <p className="text-muted-foreground mb-6">{t("about.supportPrompt")}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/store">
              <button className="px-6 py-3 bg-gradient-to-b from-yellow-600 to-yellow-800 hover:from-yellow-500 hover:to-yellow-700 text-black font-heading rounded-lg gold-glow transition-all">
                {t("about.supportExplore")}
              </button>
            </Link>
            <Link to="/contact">
              <button className="px-6 py-3 border border-purple-700 text-purple-200 hover:bg-purple-950/50 font-heading rounded-lg transition-all">
                {t("about.supportContact")}
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
